import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import {
  Search, ChevronDown, PlayCircle, Car, Building2,
  CreditCard, Ban, Key, UserCircle, LifeBuoy, MessageSquare, Sparkles
} from "lucide-react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";

const CATEGORIES = [
  { id: "empezar", label: "Empezar", icon: Sparkles, color: "text-violet-400" },
  { id: "reservar", label: "Reservar", icon: Car, color: "text-emerald-400" },
  { id: "publicar", label: "Publicar", icon: Building2, color: "text-amber-400" },
  { id: "pagos", label: "Pagos", icon: CreditCard, color: "text-sky-400" },
  { id: "cancelar", label: "Cancelaciones", icon: Ban, color: "text-rose-400" },
  { id: "gate", label: "Plazafy Gate", icon: Key, color: "text-fuchsia-400" },
  { id: "cuenta", label: "Cuenta", icon: UserCircle, color: "text-indigo-400" },
];

const FAQS = [
  // Empezar
  { cat: "empezar", q: "¿Qué es Plazafy?", a: "Plazafy es un marketplace que conecta a propietarios de plazas de garaje con conductores que necesitan aparcar. Puedes alquilar por horas, días o meses, todo desde la app." },
  { cat: "empezar", q: "¿Cómo me registro?", a: "Pulsa \"Registrarse\" en la barra superior. Puedes crear una cuenta con email y contraseña, o iniciar sesión rápido con Google o Apple. Necesitarás un teléfono y aceptar nuestros términos." },
  { cat: "empezar", q: "¿Necesito ser mayor de edad?", a: "Sí, debes tener al menos 18 años o contar con autorización legal para usar Plazafy." },
  { cat: "empezar", q: "¿Es gratis usar Plazafy?", a: "Registrarse y buscar plazas es totalmente gratis. Solo pagas cuando reservas una plaza. La comisión de servicio ya viene incluida en el precio que ves." },

  // Reservar
  { cat: "reservar", q: "¿Cómo busco una plaza?", a: "Desde la sección \"Explorar\" o el buscador de la home. Puedes filtrar por ciudad, precio, características (cubierta, vigilada, cargador EV...) o si tiene Plazafy Gate." },
  { cat: "reservar", q: "¿Cómo reservo una plaza?", a: "Entra en el detalle de la plaza, elige el tipo de reserva (hora, día o mes), las fechas, añade tu matrícula y pulsa \"Reservar ahora\". Recibirás un email de confirmación al instante." },
  { cat: "reservar", q: "¿Puedo modificar mi reserva?", a: "Sí, desde \"Mis reservas\" puedes editar la matrícula, las notas o las fechas (siempre que no se solapen con otras reservas) hasta el momento de inicio." },
  { cat: "reservar", q: "¿Cómo accedo a la plaza?", a: "Dependiendo de la plaza: el propietario te enviará las instrucciones de acceso vía mensaje, o si tiene Plazafy Gate, podrás abrir el garaje directamente desde la app." },
  { cat: "reservar", q: "¿Qué pasa si no encuentro la plaza?", a: "Usa el chat de Plazafy con el propietario para resolver dudas. Si no responde o hay un problema grave, escríbenos a hola@plazafy.es y te ayudaremos." },

  // Publicar
  { cat: "publicar", q: "¿Cómo publico mi plaza?", a: "Necesitas tener una cuenta. Pulsa \"Publicar Plaza\" en la barra superior, completa la información (dirección, fotos, precios por hora/día/mes, características) y pulsa publicar. Estará visible en minutos." },
  { cat: "publicar", q: "¿Cuánto puedo cobrar?", a: "Tú decides el precio. Te recomendamos investigar precios similares en tu zona. Puedes establecer tarifas distintas por hora, día y mes." },
  { cat: "publicar", q: "¿Qué comisión cobra Plazafy?", a: "Plazafy aplica una comisión de servicio sobre cada reserva. La comisión ya viene incluida en el precio final que paga el inquilino, tú recibes el importe neto." },
  { cat: "publicar", q: "¿Puedo gestionar la disponibilidad?", a: "Sí, desde \"Mis Plazas\" puedes activar/desactivar tu plaza en cualquier momento, editar precios, fotos, descripción y características." },
  { cat: "publicar", q: "¿Cómo cobro mis ganancias?", a: "Configura tu cuenta bancaria en tu perfil. Las transferencias se realizan automáticamente tras completarse las reservas. Puedes ver el desglose fiscal AEAT en la sección \"Ingresos\"." },

  // Pagos
  { cat: "pagos", q: "¿Qué métodos de pago aceptáis?", a: "Aceptamos tarjeta de crédito/débito vía Stripe (Visa, Mastercard, AMEX) y PayPal. Todos los pagos son seguros y encriptados." },
  { cat: "pagos", q: "¿Cuándo se carga el pago?", a: "El pago se realiza en el momento de confirmar la reserva. Recibirás un recibo PDF por email automáticamente." },
  { cat: "pagos", q: "¿Recibo factura?", a: "Sí, recibes un recibo/borrador automáticamente. Si necesitas factura oficial con datos fiscales, contáctanos en hola@plazafy.es." },

  // Cancelaciones
  { cat: "cancelar", q: "¿Cómo cancelo una reserva?", a: "Ve a \"Mis reservas\" → selecciona la reserva → pulsa \"Cancelar\". Recuerda que las cancelaciones tienen condiciones según el tiempo restante." },
  { cat: "cancelar", q: "¿Recibo reembolso al cancelar?", a: "Si cancelas con al menos 24 horas de antelación, recibes reembolso completo. Cancelaciones tardías pueden tener cargos." },
  { cat: "cancelar", q: "¿Qué pasa si el propietario cancela?", a: "Recibes el reembolso completo automáticamente. Además te ayudamos a encontrar otra plaza similar en tu zona." },

  // Plazafy Gate
  { cat: "gate", q: "¿Qué es Plazafy Gate?", a: "Es nuestro dispositivo IoT que se instala en el motor del garaje y permite abrir la puerta directamente desde la app. Sin mandos, sin llaves, sin esperar al propietario." },
  { cat: "gate", q: "¿Cómo lo solicito?", a: "Ve a la sección \"Plazafy Gate\" en el menú y únete a la lista de espera. Te contactaremos para coordinar la instalación cuando esté disponible en tu zona." },
  { cat: "gate", q: "¿Tiene coste?", a: "El dispositivo tiene una cuota mensual reducida y se instala sin coste inicial. Compensa rápidamente al automatizar el acceso y reducir tu trabajo como propietario." },

  // Cuenta
  { cat: "cuenta", q: "¿Cómo cambio mi contraseña?", a: "Ve a tu perfil → \"Cambiar contraseña\". Necesitarás introducir la actual y la nueva. Si usaste Google o Apple para registrarte, no tienes contraseña — gestiona el acceso desde ese proveedor." },
  { cat: "cuenta", q: "¿Cómo elimino mi cuenta?", a: "Escríbenos a hola@plazafy.es desde el email asociado a tu cuenta y procederemos a eliminarla en menos de 72h, cumpliendo el RGPD." },
  { cat: "cuenta", q: "¿Cómo cambio mi email o teléfono?", a: "Desde tu perfil puedes actualizar email, teléfono, nombre completo y avatar en cualquier momento." },
];

function FaqItem({ faq, isOpen, onToggle }) {
  return (
    <div className="border border-white/5 rounded-xl bg-[#231f30] overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full px-5 py-4 flex items-center justify-between gap-4 text-left hover:bg-white/[0.02] transition-colors"
        data-testid={`faq-toggle-${faq.cat}`}
      >
        <span className="text-white font-medium">{faq.q}</span>
        <ChevronDown
          className={`w-5 h-5 text-violet-400 flex-shrink-0 transition-transform ${isOpen ? "rotate-180" : ""}`}
        />
      </button>
      {isOpen && (
        <div className="px-5 pb-5 pt-0 text-gray-400 leading-relaxed border-t border-white/5">
          <p className="pt-4">{faq.a}</p>
        </div>
      )}
    </div>
  );
}

export default function Help() {
  const [activeCat, setActiveCat] = useState("empezar");
  const [search, setSearch] = useState("");
  const [openId, setOpenId] = useState(null);

  const filteredFaqs = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (term) {
      return FAQS.filter(f =>
        f.q.toLowerCase().includes(term) || f.a.toLowerCase().includes(term)
      );
    }
    return FAQS.filter(f => f.cat === activeCat);
  }, [search, activeCat]);

  return (
    <div className="pt-24 min-h-screen bg-[#1a1625]">
      {/* Hero */}
      <div className="relative overflow-hidden border-b border-white/5">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-violet-500/10 rounded-full blur-3xl pointer-events-none" />
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-violet-500/10 border border-violet-500/20 text-violet-300 text-sm mb-5">
            <LifeBuoy className="w-4 h-4" />
            Centro de Ayuda
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-white mb-3">
            ¿En qué <span className="text-violet-400">te ayudamos</span>?
          </h1>
          <p className="text-gray-400 max-w-xl mx-auto mb-8">
            Aprende a usar Plazafy paso a paso. Vídeo tutorial, manuales y respuestas a las preguntas más comunes.
          </p>
          <div className="relative max-w-xl mx-auto">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <Input
              type="text"
              placeholder="Buscar en el centro de ayuda..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="help-search-input"
              className="pl-12 h-14 bg-[#231f30] border-white/10 text-white placeholder:text-gray-500 text-base"
            />
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 space-y-16">
        {/* Video tutorial */}
        <section data-testid="help-video-section">
          <div className="flex items-end justify-between mb-5 gap-4">
            <div>
              <div className="flex items-center gap-2 text-violet-400 text-sm font-medium mb-1">
                <PlayCircle className="w-4 h-4" />
                Vídeo tutorial
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold text-white">
                Cómo reservar en Plazafy
              </h2>
            </div>
            <span className="text-gray-500 text-sm hidden sm:block">~40 segundos</span>
          </div>
          <div className="relative rounded-2xl overflow-hidden border border-white/10 bg-black aspect-video shadow-2xl shadow-violet-500/10">
            <video
              src="/videos/tutorial_inquilino.mp4"
              controls
              preload="metadata"
              poster=""
              className="w-full h-full"
              data-testid="help-tutorial-video"
            >
              Tu navegador no soporta vídeo HTML5.
            </video>
          </div>
          <p className="text-gray-500 text-sm mt-3 text-center">
            ¿Aún no tienes cuenta?{" "}
            <Link to="/register" className="text-violet-400 hover:text-violet-300 underline decoration-violet-400/30">
              Regístrate gratis
            </Link>
          </p>
        </section>

        {/* Quick guides cards */}
        <section>
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-2">Guías rápidas</h2>
          <p className="text-gray-500 mb-6">Lo esencial en 60 segundos.</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { icon: Car, title: "Reservar una plaza", desc: "Explora, elige fechas y reserva en 3 clics.", color: "from-violet-500/20 to-violet-500/0", iconColor: "text-violet-400" },
              { icon: Building2, title: "Publicar tu plaza", desc: "Genera ingresos con tu plaza vacía. Te lleva 5 minutos.", color: "from-amber-500/20 to-amber-500/0", iconColor: "text-amber-400" },
              { icon: CreditCard, title: "Pagos seguros", desc: "Stripe y PayPal. Comisión incluida en el precio.", color: "from-emerald-500/20 to-emerald-500/0", iconColor: "text-emerald-400" },
              { icon: Key, title: "Plazafy Gate", desc: "Abre el garaje desde la app, sin mandos.", color: "from-fuchsia-500/20 to-fuchsia-500/0", iconColor: "text-fuchsia-400" },
              { icon: Ban, title: "Cancelaciones", desc: "Gratis hasta 24h antes del inicio.", color: "from-rose-500/20 to-rose-500/0", iconColor: "text-rose-400" },
              { icon: UserCircle, title: "Tu cuenta", desc: "Edita perfil, contraseña, datos fiscales.", color: "from-sky-500/20 to-sky-500/0", iconColor: "text-sky-400" },
            ].map(card => {
              const Icon = card.icon;
              return (
                <div
                  key={card.title}
                  className={`group p-5 rounded-2xl bg-[#231f30] border border-white/5 hover:border-violet-500/30 transition-all relative overflow-hidden`}
                >
                  <div className={`absolute inset-0 bg-gradient-to-br ${card.color} opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none`} />
                  <div className="relative">
                    <div className="w-10 h-10 rounded-lg bg-white/5 flex items-center justify-center mb-3">
                      <Icon className={`w-5 h-5 ${card.iconColor}`} />
                    </div>
                    <h3 className="text-white font-semibold mb-1">{card.title}</h3>
                    <p className="text-gray-400 text-sm leading-relaxed">{card.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* FAQ */}
        <section>
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-2">Preguntas frecuentes</h2>
          <p className="text-gray-500 mb-6">
            {search ? `Resultados para "${search}"` : "Selecciona una categoría:"}
          </p>

          {!search && (
            <div className="flex flex-wrap gap-2 mb-6">
              {CATEGORIES.map(cat => {
                const Icon = cat.icon;
                const isActive = activeCat === cat.id;
                return (
                  <button
                    key={cat.id}
                    onClick={() => { setActiveCat(cat.id); setOpenId(null); }}
                    data-testid={`help-cat-${cat.id}`}
                    className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all border ${
                      isActive
                        ? "bg-violet-500 border-violet-500 text-white"
                        : "bg-[#231f30] border-white/5 text-gray-400 hover:text-white hover:border-violet-500/30"
                    }`}
                  >
                    <Icon className={`w-4 h-4 ${isActive ? "text-white" : cat.color}`} />
                    {cat.label}
                  </button>
                );
              })}
            </div>
          )}

          {filteredFaqs.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              No encontramos resultados para tu búsqueda.{" "}
              <button
                onClick={() => setSearch("")}
                className="text-violet-400 hover:text-violet-300 underline"
              >
                Limpiar búsqueda
              </button>
            </div>
          ) : (
            <div className="space-y-2">
              {filteredFaqs.map((faq, idx) => {
                const key = `${faq.cat}-${idx}`;
                return (
                  <FaqItem
                    key={key}
                    faq={faq}
                    isOpen={openId === key}
                    onToggle={() => setOpenId(openId === key ? null : key)}
                  />
                );
              })}
            </div>
          )}
        </section>

        {/* CTA contact */}
        <section className="rounded-2xl p-8 sm:p-12 bg-gradient-to-br from-violet-600/20 to-violet-900/10 border border-violet-500/20 text-center">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-violet-500/20 mb-4">
            <MessageSquare className="w-6 h-6 text-violet-400" />
          </div>
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-2">
            ¿No encuentras lo que buscas?
          </h2>
          <p className="text-gray-400 mb-6 max-w-md mx-auto">
            Habla con Plazi, nuestro asistente, o escríbenos directamente.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <a href="mailto:hola@plazafy.es">
              <Button
                className="bg-violet-500 hover:bg-violet-600 text-white"
                data-testid="help-contact-email-btn"
              >
                Escríbenos a hola@plazafy.es
              </Button>
            </a>
            <p className="text-gray-500 text-sm">
              o usa el chat 🤖 abajo a la derecha
            </p>
          </div>
        </section>
      </div>
    </div>
  );
}
